/**
 * Shared helpers for hierarchical inventory master entities
 * (material groups, categories, item classes).
 */

export function toMasterList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  return data.items ?? data.data ?? [];
}

export function masterRowId(row, idKey) {
  return row?.[idKey] ?? row?.id;
}

export function masterName(row, nameKey) {
  return row?.[nameKey] ?? row?.name ?? '';
}

export function masterCode(row, codeKey) {
  return row?.[codeKey] ?? row?.code ?? '';
}

export function lastModifiedValue(row) {
  if (!row) return null;
  return row.updated_at ?? row.modified_at ?? row.last_modified ?? null;
}

export function resolveParentId(row, parentKey) {
  if (!row) return '';
  const nested = row[parentKey.replace('_id', '')];
  const raw = row[parentKey]
    ?? nested?.[parentKey]
    ?? nested?.id
    ?? nested?.[`${parentKey.replace('_id', '')}_id`]
    ?? '';
  return raw === '' || raw == null ? '' : String(raw);
}

export function parentSelectOptions(items, { idKey, nameKey, parentKey, excludeId = null } = {}) {
  const exclude = excludeId != null ? String(excludeId) : null;
  return [
    { value: '', label: '— None —' },
    ...items
      .filter(row => exclude == null || String(masterRowId(row, idKey)) !== exclude)
      .map(row => ({
        value: String(masterRowId(row, idKey)),
        label: masterName(row, nameKey),
      })),
  ];
}

/** Collects an entity id and all of its descendant ids (prevents circular parent assignment). */
export function collectDescendantIds(items, rootId, { idKey, parentKey } = {}) {
  const ids = new Set([String(rootId)]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of items) {
      const id = String(masterRowId(row, idKey));
      const parentId = resolveParentId(row, parentKey);
      if (parentId && ids.has(parentId) && !ids.has(id)) {
        ids.add(id);
        changed = true;
      }
    }
  }
  return ids;
}

export function buildMasterHierarchyLabel(row, items, { idKey, nameKey, codeKey, parentKey } = {}) {
  const parts = [];
  let current = row;
  const visited = new Set();
  while (current) {
    const id = String(masterRowId(current, idKey));
    if (visited.has(id)) break;
    visited.add(id);
    parts.unshift(masterName(current, nameKey));
    const parentId = resolveParentId(current, parentKey);
    if (!parentId) break;
    current = items.find(item => String(masterRowId(item, idKey)) === parentId);
  }
  const code = codeKey ? masterCode(row, codeKey) : '';
  const path = parts.join(' › ');
  return code ? `${path} (${code})` : path;
}

export function hierarchicalParentSelectOptions(items, {
  idKey,
  nameKey,
  codeKey,
  parentKey,
  excludeIds = [],
} = {}) {
  const excluded = new Set(excludeIds.filter(id => id != null && id !== '').map(String));
  return [
    { value: '', label: '— None (top-level) —' },
    ...items
      .filter(row => !excluded.has(String(masterRowId(row, idKey))))
      .map(row => ({
        value: String(masterRowId(row, idKey)),
        label: buildMasterHierarchyLabel(row, items, { idKey, nameKey, codeKey, parentKey }),
      })),
  ];
}

export function resolveParentLabel(row, items, { idKey, nameKey, parentKey }) {
  const parentId = resolveParentId(row, parentKey);
  if (!parentId) return '—';
  const parent = items.find(item => String(masterRowId(item, idKey)) === parentId);
  if (parent) return masterName(parent, nameKey);
  const nested = row[parentKey.replace('_id', '')];
  if (nested && typeof nested === 'object') {
    return nested[nameKey] ?? nested.name ?? '—';
  }
  return '—';
}

/** Missing `is_active` is treated as active — same convention as vendors/items. */
export function isMasterActive(row) {
  return row?.is_active !== false;
}

export function filterActiveMasters(list) {
  return (Array.isArray(list) ? list : []).filter(isMasterActive);
}

export function inactiveMasterMessage(label) {
  return `${label || 'This record'} is inactive and cannot be used.`;
}

export function toSelectOptions(items, { idKey, nameKey, codeKey, activeOnly = true } = {}) {
  const list = activeOnly
    ? items.filter(isMasterActive)
    : items;
  return list.map(row => {
    const id = masterRowId(row, idKey);
    const name = masterName(row, nameKey);
    const code = codeKey ? masterCode(row, codeKey) : '';
    return {
      value: id,
      label: code ? `${name} (${code})` : name,
    };
  });
}

export function filterMasterRows(rows, { search = '', status = '', searchKeys = [] }) {
  let result = rows;
  const q = search.trim().toLowerCase();
  if (q) {
    result = result.filter(row =>
      searchKeys.some(key => String(row[key] ?? '').toLowerCase().includes(q)),
    );
  }
  if (status === 'active') {
    result = result.filter(row => row.is_active !== false);
  } else if (status === 'inactive') {
    result = result.filter(row => row.is_active === false);
  }
  return result;
}

/** Map API `id` → `uom_id` for consistent frontend references */
export function normalizeUom(row) {
  if (!row || typeof row !== 'object') return row;
  const uom_id = row.uom_id ?? row.id;
  return uom_id == null ? row : { ...row, uom_id };
}

export function normalizeUomList(data) {
  return toMasterList(data).map(normalizeUom);
}

export function uomRowId(row) {
  return row?.uom_id ?? row?.id;
}

/** Map API `id` → `tax_id` for consistent frontend references */
export function normalizeTaxMaster(row) {
  if (!row || typeof row !== 'object') return row;
  const tax_id = row.tax_id ?? row.id;
  return tax_id == null ? row : { ...row, tax_id };
}

export function normalizeTaxMasterList(data) {
  return toMasterList(data).map(normalizeTaxMaster);
}

export function taxMasterRowId(row) {
  return row?.tax_id ?? row?.id;
}

/** Map API `id` → `category_id` for consistent frontend references */
export function normalizeCategory(row) {
  if (!row || typeof row !== 'object') return row;
  const category_id = row.category_id ?? row.id;
  return category_id == null ? row : { ...row, category_id };
}

export function normalizeCategoryList(data) {
  return toMasterList(data).map(normalizeCategory);
}

export function categoryRowId(row) {
  return row?.category_id ?? row?.id;
}

/** Item class — API fields class_code/class_name/parent_class_id + frontend item_class_id */
export function normalizeItemClass(row) {
  if (!row || typeof row !== 'object') return row;
  const item_class_id = row.item_class_id ?? row.id;
  const class_code = row.class_code ?? row.item_class_code ?? '';
  const class_name = row.class_name ?? row.item_class_name ?? '';
  const parent_class_id = row.parent_class_id
    ?? row.parent_item_class_id
    ?? row.parent_class?.id
    ?? null;
  const base = {
    ...row,
    class_code,
    class_name,
    parent_class_id: parent_class_id == null ? null : parent_class_id,
  };
  return item_class_id == null ? base : { ...base, item_class_id };
}

export function normalizeItemClassList(data) {
  return toMasterList(data).map(normalizeItemClass);
}

export function itemClassRowId(row) {
  return row?.item_class_id ?? row?.id;
}

/** Material group — API fields group_code/group_name/parent_group_id */
export function normalizeMaterialGroup(row) {
  if (!row || typeof row !== 'object') return row;
  const material_group_id = row.material_group_id ?? row.id;
  const group_code = row.group_code ?? row.material_group_code ?? '';
  const group_name = row.group_name ?? row.material_group_name ?? '';
  const parent_group_id = row.parent_group_id
    ?? row.parent_material_group_id
    ?? row.parent_group?.id
    ?? null;
  const base = {
    ...row,
    group_code,
    group_name,
    parent_group_id: parent_group_id == null ? null : parent_group_id,
  };
  return material_group_id == null ? base : { ...base, material_group_id };
}

export function normalizeMaterialGroupList(data) {
  return toMasterList(data).map(normalizeMaterialGroup);
}

export function materialGroupRowId(row) {
  return row?.material_group_id ?? row?.id;
}

export function resolveMaterialGroupParentLabel(row, allGroups = []) {
  if (!row) return '—';
  const nested = row.parent_group;
  if (nested && typeof nested === 'object') {
    return nested.group_name ?? nested.material_group_name ?? nested.name ?? '—';
  }
  const parentId = row.parent_group_id ?? row.parent_material_group_id;
  if (!parentId) return '—';
  const parent = allGroups.find(g => String(materialGroupRowId(g)) === String(parentId));
  return parent?.group_name ?? parent?.material_group_name ?? '—';
}

/**
 * Extracts a normalized { items, total } from a raw paginated API response.
 * Handles three response shapes: bare array, { items, total }, { data: [...] }.
 * Falls back to estimating total when the backend omits it.
 */
export function toInventoryPage(raw, { page, pageSize }) {
  const items = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
  const total = typeof raw?.total === 'number'
    ? raw.total
    : items.length < pageSize
      ? page * pageSize + items.length
      : (page + 1) * pageSize + 1;
  return { items, total };
}

export function normalizeMasterBody(data, { parentKey, nullableFields = [] }) {
  const body = { ...data };
  for (const key of nullableFields) {
    if (body[key] === '' || body[key] === undefined) body[key] = null;
  }
  if (parentKey && !body[parentKey]) body[parentKey] = null;
  return body;
}
